import React, { useCallback, useMemo, useState } from "react";

import { number, object, string } from "yup";
import { useDispatch, useSelector } from "react-redux";

import WaitForSync from "components/LoadingSpinner/WaitForSync";

import { AgGridPanels } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import { v4 as uuidv4 } from "uuid";

import {
  getConsignments,
  getCurrentRoundsList,
  getCurrentSale,
  selectClearingSaleConsignmentOptions,
} from "selectors";

import { useCounter, useFieldValue, useIsAgGridToolPanelVisible } from "hooks";
import { GridApi } from "ag-grid-community";
import { Form, Formik } from "formik";
import {
  CheckBox,
  CheckBoxBase,
  Input,
  SelectField,
  useSubmitHandler,
  withNamespace,
} from "components/Form/FormikControls";
import Grid from "@material-ui/core/Grid";
import { Paper } from "components/Paper";
import { PricingType } from "components/SellingSaleLotForm/PricingType";
import { PricingTypes } from "constants/pricingTypes";
import { FormikHelpers } from "formik/dist/types";
import { LotStatus } from "constants/clearingSaleAttributes";
import { Button, SecondaryButton } from "components/Form";
import {
  addSaleLotWithPens,
  addSaleLotWithPensWithBidderRegistration,
} from "actions";
import { Row } from "components/Layout";

import { BusinessField } from "components/Form/Fields";
import { calculateTotalPriceCents } from "lib";

import { QuickSellBidderAndBuyerSaleLotFields } from "components/QuickEditSaleLotForm/BidderAndBuyerFields";
import { SellSafeOptionToggler } from "components/SellSafe/SellSafe";
import { Divider } from "@material-ui/core";
import { BUYER } from "constants/businesses";

interface CalculatePriceOption {
  unitPrice: number;
  quantity: number;
  pricing_type_id: number;
  total_mass_grams: number;
}

interface QuickCreateFormProps {
  namespace: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React state setter return type
  setStateValue(value: boolean): any;
}

function QuickCreateForm(props: QuickCreateFormProps): React.JSX.Element {
  const { namespace: ns = "", setStateValue } = props;

  const quantity: number = useFieldValue(withNamespace(ns, "quantity"));

  const consignmentOptions = useSelector(selectClearingSaleConsignmentOptions);

  const currentSale = useSelector(getCurrentSale);

  const isUsingRegisteredBidders = currentSale
    ? currentSale.using_registered_bidders
    : false;

  return (
    <>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="flex-end"
        item
        xs={12}
      >
        <Grid item xs={10}>
          <Input
            label="Lot Number"
            type="number"
            name={withNamespace(ns, "lot_number")}
            placeholder="Automatically Assigned"
          />
        </Grid>
        <Grid item xs={2}>
          <Input type="text" name={withNamespace(ns, "lotNumberSuffix")} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SelectField
          name="consignment_id"
          options={consignmentOptions}
          label="Vendor"
          placeholder="Select Vendor"
          required
          isClearable
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          label="Lot Title"
          name={withNamespace(ns, "clearingSaleAttributes.title")}
          autoFocus
          required
        />
      </Grid>
      <Grid item xs={12}>
        {isUsingRegisteredBidders ? (
          <QuickSellBidderAndBuyerSaleLotFields disableInitialAutoFocus />
        ) : (
          <BusinessField
            name="buyerId"
            label="Buyer"
            sortRecentBusinessType={BUYER}
            activeOnly
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <PricingType
          quantity={quantity}
          namespace=""
          hideFields={{
            quantity: false,
            outerPricingType: true,
            innerPricingType: false,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <CheckBox
          name={withNamespace(ns, "isGSTExempt")}
          label="GST Exempt"
          tooltip={null}
          disabled={false}
          dataTour={undefined}
          onChangeExtra={(value: boolean) => setStateValue(value)}
        />
      </Grid>
      <Grid item xs={12}>
        <SellSafeOptionToggler
          name={withNamespace(ns, "clearingSaleAttributes.sellSafeCategory")}
        />
      </Grid>
    </>
  );
}

function FormButtons(): React.JSX.Element {
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  useSubmitHandler(isSubmitEnabled, setIsSubmitEnabled);
  return (
    <Row>
      <SecondaryButton data-tour="clear" type="reset">
        Clear
      </SecondaryButton>
      <Button data-tour="create" type="submit" disabled={!isSubmitEnabled}>
        Create
      </Button>
    </Row>
  );
}

const validationSchema = object().shape({
  consignment_id: string().required("Required").typeError("Required"),
  clearingSaleAttributes: object().shape({
    title: string().required("Required"),
  }),
  quantity: number().required("Required"),
  bidderNumber: number().nullable().typeError("This field must be a number"),
  buyerId: string().nullable(),
});

type FormValues = Partial<
  Pick<
    SaleLot,
    | "lot_number"
    | "lot_number_suffix"
    | "total_price_cents"
    | "consignment_id"
    | "clearingSaleAttributes"
    | "quantity"
    | "sale_round_id"
    | "isGSTExempt"
    | "unit_price"
    | "pricing_type_id"
  >
> & {
  bidderNumber?: number;
  buyerId?: string;
};

function QuickCreateClearingSaleSaleLotPanel(): React.JSX.Element {
  const dispatch = useDispatch();
  const currentRounds = useSelector(getCurrentRoundsList);
  const consignmentsById = useSelector(getConsignments);
  const consignmentIds = Object.keys(consignmentsById);
  const [invalidationCount, invalidate] = useCounter();
  // TODO: when we implement quick sell settings - we can move these to redux state settings
  const [isGstExempt, setIsGstExempt] = useState(false);
  const [rememberGstSetting, setRememberGstSetting] = useState(false);

  const initialValues: FormValues = useMemo(
    () => ({
      bidderId: null,
      buyer_id: null,
      // if there is only one vendor in the sale - default to that vendor
      consignment_id: consignmentIds.length === 1 ? consignmentIds[0] : null,
      clearingSaleAttributes: {
        title: "",
        status: LotStatus.PENDING,
        sellSafeCategory: null,
      },
      pricing_type_id: PricingTypes.GROSS,
      quantity: 1, // Seems like a sensible default
      sale_round_id: currentRounds[0], // Clearing Sales only have one round - CLEARING SALE
      bidderRegistration: null,
      unit_price: null,
      isGSTExempt: false,
    }),
    [consignmentIds, currentRounds],
  );

  const onSubmit = useCallback(
    (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      const newSaleLotId = uuidv4();
      const priceObject: CalculatePriceOption = {
        unitPrice: values.unit_price,
        quantity: values.quantity,
        pricing_type_id: values.pricing_type_id,
        total_mass_grams: 0,
      };
      const totalPriceCents = calculateTotalPriceCents(priceObject);

      const { buyerId, ...rest } = values;

      const data = {
        ...rest,
        total_price_cents: totalPriceCents,
        buyer_id: buyerId,
      };

      if (data.bidderNumber) {
        dispatch(
          addSaleLotWithPensWithBidderRegistration(
            newSaleLotId,
            data as FormValues & { bidderNumber: number },
          ),
        );
      } else {
        dispatch(addSaleLotWithPens(newSaleLotId, data));
      }

      // Reset gst exempt state if true and not remembering it
      if (!rememberGstSetting && isGstExempt) {
        setIsGstExempt(false);
      }

      formikHelpers.resetForm({
        values: {
          ...initialValues,
          consignment_id: values.consignment_id,
          isGSTExempt: rememberGstSetting ? isGstExempt : false,
        },
      });
    },
    [dispatch, initialValues, rememberGstSetting, isGstExempt],
  );

  const onReset = useCallback(() => {
    // Invalidate will cause the QuickCreateForm to re-mount and allow it to present
    // as a new form, including focus on the preferred input and any other initial presentation logic.
    invalidate();
  }, [invalidate]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      onReset={onReset}
      validationSchema={validationSchema}
    >
      <Form>
        <Paper data-tour="quickCreateForm">
          <Grid container spacing={2} justifyContent="center">
            <Grid item container xs={12} spacing={1}>
              <QuickCreateForm
                namespace=""
                key={invalidationCount}
                setStateValue={setIsGstExempt}
              />
            </Grid>
            <Grid item xs={12}>
              <FormButtons />
            </Grid>
            <Grid container item xs={12}>
              <CheckBoxBase
                onChange={() => setRememberGstSetting(!rememberGstSetting)}
                value={rememberGstSetting}
                tooltip="Setting this will remember what has been set for GST exempt when creating the next sale lot"
                label="Remember GST exempt"
                dataTour="rememberGstExempt"
                disabled={false}
                name="rememberGstExempt"
              />
            </Grid>
          </Grid>
        </Paper>
      </Form>
    </Formik>
  );
}

interface LoadingWrapperProps {
  api: GridApi;
}

function LoadingWrapper(props: LoadingWrapperProps): React.JSX.Element {
  const { api } = props;

  const isToolPanelVisible = useIsAgGridToolPanelVisible(
    api,
    AgGridPanels.QUICK_CREATE,
  );
  if (isToolPanelVisible) {
    return (
      <WaitForSync
        requiredData={[
          ApiModel.DEPLOYMENTS,
          ApiModel.SALEYARDS,
          ApiModel.BUSINESSES,
          ApiModel.SALE_LOTS,
          ApiModel.ROUNDS,
          ApiModel.SALES,
        ]}
      >
        <QuickCreateClearingSaleSaleLotPanel />
      </WaitForSync>
    );
  }
  // When the tool panel is not visible, we need to return something to
  // for AgGrid for it to be willing to show the tool panel when the user opens it
  return <p />;
}

export default LoadingWrapper;
